import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { Container, TextField, Select, Typography, Grid, MenuItem, FormControl, InputLabel, Button } from '@material-ui/core';

import { getTodayAsISODate } from '../../../utils/DateUtils';
import ServiceAPI from '../../../service/ServiceAPI';
import LoadingIndicator from '../../utils/LoadingIndicator';
import Constants from '../../../utils/Constants';
import { Status as HoseStatus, Coupling as HoseCoupling, Material as HoseMaterial } from '../../../enums/HoseEnum';

const GRID_SPACING = 4;

const useStyles = makeStyles((theme) => ({
  sectionHeading: {
    color: '#264e98',
    fontSize: '1.8rem',
    letterSpacing: '-0.025em',
    fontWeight: 800,
    fontFamily: 'Verdana'
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  blockHeading: {
    textAlign: 'left',
    borderLeft: '5px solid red',
    paddingLeft: '1em',
    marginTop: '2em',
    textTransform: 'uppercase'
  }
}));


const EditHose = () => {
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [loading, setLoading] = useState(true);
  const [hose, setHose] = useState(null);
  const [hoseModified, setHoseModified] = useState(0); // A count of thec changes made.

  useEffect(() => {
    fetchHose();
  }, []);

  /**
   * 
   */
  const fetchHose = () => {
    ServiceAPI.HoseManagement().getHose(params.hoseId).then(
      hoseModel => {  
        setHose(hoseModel);  
        setLoading(false);
      } 
    )
  }

  /**
   * 
   */
  const handleClick_btnSave = () => {
    if (hoseModified < 1) {
      return; // There has been no edits, shouldnt have been able to click the button.
    }

    setLoading(true);

    // Update any auto values based on user changes
    if (hose.getStatus() == HoseStatus.DECOMMISSIONED && hose.getDecommissionedDate() == null) {
      hose.setDecommissionedDate(getTodayAsISODate());
    }

    ServiceAPI.HoseManagement().updateHose(hose).then((result) => {
      if (result) {
        navigate(Constants.RoutePath.HoseManagement.Listing);
      }

      // TODO: Need to put in notification of save error. At
      //       this stage it will just a stuck loading screen.
    });
  }

  /**
   * 
   */
  const handleClick_btnBack = () => {
    navigate(Constants.RoutePath.HoseManagement.Listing);
  }

  /**
   * 
   * @param {*} e 
   */
  const handleChange_hoseName = (e) => {
    hose.setHoseName(e.target.value);

    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @param {*} e 
   * @returns 
   */
  const handleChange_hoseLength = (e) => {
    if (e.target.value.match('[^0-9.]')) {
      return;
    }

    hose.setLengthM(e.target.value);
    
    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @param {*} e 
   */
  const handleChange_hoseDiameter = (e) => {
    if (e.target.value.match('[^0-9]')) {
      return;
    }

    hose.setDiameter(e.target.value);
    
    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @param {*} e 
   */
  const handleChange_couplingType = (e) => {
    hose.setCouplingType(e.target.value);

    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @param {*} e 
   */
  const handleChange_material = (e) => {
    hose.setMaterial(e.target.value);

    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @param {*} e 
   */
  const handleChange_hoseStatus = (e) => {
    hose.setStatus(e.target.value);

    setHose(hose);
    setHoseModified(hoseModified + 1);
  }
    /**
   * 
   * @param {*} e 
   */
  const handleChange_commissionedDate = (e) => {
    hose.setInServiceDate(e.target.value);

    setHose(hose);
    setHoseModified(hoseModified + 1);
  }

  /**
   * 
   * @returns 
   */
  const render = () => {
    if (loading) {
      return <div><LoadingIndicator isOpen={true} /></div>;
    } 

    return (
      <Container className={classes.container}>
        <Grid container justifyContent="space-between" className={classes.header} xs={12} direction='row'>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.sectionHeading}>
              Hose Details
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" onClick={handleClick_btnBack}>
              Back
            </Button>
          </Grid>
          <Grid item xs={3}>
            <Button variant="contained" color="primary" disabled={ (hoseModified < 1 ? true : false) } onClick={handleClick_btnSave}>
              Save
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <h3 className={classes.blockHeading}>Identity</h3>
        </Grid>
        <Grid container xs={12} direction={'row'} spacing={GRID_SPACING}>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="filled" label="ID" value={hose.getId()} InputProps={{readOnly: true}} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth variant="outlined" label="Hose Name" value={hose.getHoseName()} onChange={handleChange_hoseName}/>
          </Grid>
        </Grid>

        <Grid item>
          <h3 className={classes.blockHeading}>Physical Specifications</h3>
        </Grid>
        <Grid container xs={12} direction={'row'} spacing={GRID_SPACING}>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="outlined" label="Length (m)" value={hose.getLengthM()} onChange={handleChange_hoseLength}/>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="outlined" label="Diameter (mm)" value={hose.getDiameter()} onChange={handleChange_hoseDiameter}/>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="lblIdCouplingType">Coupling Type</InputLabel>
              <Select
                id="cmbCouplingType"
                value={hose.getCouplingType()}
                label="Coupling Type"
                labelId="lblIdCouplingType"
                onChange={handleChange_couplingType}
              >
                <MenuItem value={HoseCoupling.EXTERNAL_LUG}>{HoseCoupling.EXTERNAL_LUG}</MenuItem>
                <MenuItem value={HoseCoupling.STORZ}>{HoseCoupling.STORZ}</MenuItem>
                <MenuItem value={HoseCoupling.TPI5}>{HoseCoupling.TPI5}</MenuItem>
                <MenuItem value={HoseCoupling.TPI3}>{HoseCoupling.TPI3}</MenuItem>
                <MenuItem value={HoseCoupling.OTHER}>{HoseCoupling.OTHER}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="lblIdMaterial">Material</InputLabel>
              <Select
                id="cmbMaterial"
                value={hose.getMaterial()}
                label="Material"
                labelId="lblIdMaterial"
                onChange={handleChange_material}
              >
                <MenuItem value={HoseMaterial.CANVAS}>{HoseMaterial.CANVAS}</MenuItem>
                <MenuItem value={HoseMaterial.DURALINE}>{HoseMaterial.DURALINE}</MenuItem>
                <MenuItem value={HoseMaterial.OTHER}>{HoseMaterial.OTHER}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid item>
          <h3 className={classes.blockHeading}>Service Record</h3>
        </Grid>
        <Grid container xs={12} direction={'row'} spacing={GRID_SPACING}>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="lblIdStatus">Status</InputLabel>
              <Select
                id="cmdOpsStatus"
                value={hose.getStatus()}
                label="Status"
                labelId="lblIdStatus"
                onChange={handleChange_hoseStatus}
              >
                <MenuItem value={HoseStatus.IN_SERVICE}>
                  {HoseStatus.toDisplayValue(HoseStatus.IN_SERVICE)}
                </MenuItem>
                <MenuItem value={HoseStatus.OUT_OF_SERVICE}>
                  {HoseStatus.toDisplayValue(HoseStatus.OUT_OF_SERVICE)}
                </MenuItem>
                <MenuItem value={HoseStatus.DECOMMISSIONED}>
                  {HoseStatus.toDisplayValue(HoseStatus.DECOMMISSIONED)}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField 
              fullWidth 
              variant="outlined" 
              label="Commissioned Date" 
              type="date" 
              value={hose.getInServiceDate()} 
              onChange={handleChange_commissionedDate}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField 
              fullWidth 
              variant="filled" 
              label="Decommissioned Date" 
              type="date" 
              value={hose.getDecommissionedDate()} 
              InputLabelProps={{ shrink: true }} 
              InputProps={{readOnly: true}} 
            />
          </Grid>
        </Grid>

        <Grid item>
          <h3 className={classes.blockHeading}>Audit</h3>
        </Grid>
        <Grid container xs={12} direction={'row'} spacing={GRID_SPACING}>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="filled" label="Hose Created" value={hose.getCreatedAt()} InputProps={{readOnly: true}} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="filled" label="Created By" value={hose.getCreatedBy()} InputProps={{readOnly: true}} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="filled" label="Lasted Updated" value={hose.getUpdatedAt()} InputProps={{readOnly: true}} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth variant="filled" label="Updated By" value={hose.getUpdatedBy()} InputProps={{readOnly: true}} />
          </Grid>          
        </Grid>

      </Container>
    );
  }

  return render();
}

export default EditHose;